export type AutomationStatus =
  | "Fully Automated"
  | "In Training"
  | "Out of Scope";

// New API response schema
export interface DashboardStatsResult {
  categoryId: string;
  errorFreeChecks: number;
  humanCaughtLowRisk: number;
  humanCaughtHighRisk: number;
  aiReviewSpeed: number;
  auditLog: number;
  status: AutomationStatus;
  weeksWithoutHighRisk: number;
}

export interface DashboardStatsResponse {
  automationPercentage: number;
  breakdownNumbers: {
    full: number;
    training: number;
    outOfScope: number;
  };
  results: DashboardStatsResult[];
}

// Display task format for the table
export interface AutomationTask {
  id: string;
  categoryId: string;
  taskCategory: string;
  description?: string;
  errorFreeChecks: number;
  humanCaughtLowRisk: number;
  humanCaughtHighRisk: number;
  aiReviewSpeed: number;
  auditLog: number;
  status: AutomationStatus;
  weeksWithoutHighRisk: number;
}

export interface AutomationSummary {
  currentAutomationPercent: number;
  fullyAutomatedCount: number;
  inTrainingCount: number;
  outOfScopeCount: number;
}

export interface MonthOption {
  label: string;
  value: string;
}

export const MONTH_OPTIONS: MonthOption[] = [
  { label: "January 2026", value: "2026-01" },
  { label: "December 2025", value: "2025-12" },
  { label: "November 2025", value: "2025-11" },
  { label: "October 2025", value: "2025-10" },
  { label: "September 2025", value: "2025-09" },
  { label: "August 2025", value: "2025-08" },
];

export const MOCK_SUMMARY: AutomationSummary = {
  currentAutomationPercent: 60,
  fullyAutomatedCount: 6,
  inTrainingCount: 4,
  outOfScopeCount: 2,
};

export const MOCK_TASKS: AutomationTask[] = [
  // Fully Automated Tasks
  {
    id: "task-1",
    categoryId: "AT1",
    taskCategory: "Previous Feedback Implemented",
    description:
      "Checks if feedback from previous reviews has been implemented in the current version",
    errorFreeChecks: 398,
    humanCaughtLowRisk: 0,
    humanCaughtHighRisk: 0,
    aiReviewSpeed: 108,
    auditLog: 130,
    status: "Fully Automated",
    weeksWithoutHighRisk: 12,
  },
  {
    id: "task-2",
    categoryId: "AT2",
    taskCategory: "Copy Errors",
    description: "Detects spelling, grammar, and copy inconsistencies",
    errorFreeChecks: 265,
    humanCaughtLowRisk: 0,
    humanCaughtHighRisk: 0,
    aiReviewSpeed: 228,
    auditLog: 95,
    status: "Fully Automated",
    weeksWithoutHighRisk: 10,
  },
  {
    id: "task-3",
    categoryId: "AT3",
    taskCategory: "Brand Logo Usage",
    description: "Validates correct brand logo placement and sizing",
    errorFreeChecks: 154,
    humanCaughtLowRisk: 0,
    humanCaughtHighRisk: 0,
    aiReviewSpeed: 258,
    auditLog: 59,
    status: "Fully Automated",
    weeksWithoutHighRisk: 8,
  },
  {
    id: "task-4",
    categoryId: "AT4",
    taskCategory: "Validate SKU/Product Variants",
    description: "Ensures SKU numbers and product variants are accurate",
    errorFreeChecks: 203,
    humanCaughtLowRisk: 0,
    humanCaughtHighRisk: 0,
    aiReviewSpeed: 270,
    auditLog: 69,
    status: "Fully Automated",
    weeksWithoutHighRisk: 9,
  },
  {
    id: "task-5",
    categoryId: "AT5",
    taskCategory: "Claim Language Approved",
    description: "Verifies marketing claims are pre-approved",
    errorFreeChecks: 103,
    humanCaughtLowRisk: 0,
    humanCaughtHighRisk: 0,
    aiReviewSpeed: 108,
    auditLog: 32,
    status: "Fully Automated",
    weeksWithoutHighRisk: 11,
  },
  {
    id: "task-6",
    categoryId: "AT6",
    taskCategory: "Brand Standard Adherence",
    description: "Checks overall compliance with brand guidelines",
    errorFreeChecks: 374,
    humanCaughtLowRisk: 0,
    humanCaughtHighRisk: 0,
    aiReviewSpeed: 288,
    auditLog: 131,
    status: "Fully Automated",
    weeksWithoutHighRisk: 14,
  },

  // In Training Tasks
  {
    id: "task-7",
    categoryId: "AT7",
    taskCategory: "Assets Match Brief Specs",
    description: "Validates assets against creative brief specifications",
    errorFreeChecks: 81,
    humanCaughtLowRisk: 15,
    humanCaughtHighRisk: 6,
    aiReviewSpeed: 144,
    auditLog: 44,
    status: "In Training",
    weeksWithoutHighRisk: 2,
  },
  {
    id: "task-8",
    categoryId: "AT8",
    taskCategory: "Retailer/Channel Requirements Met",
    description: "Ensures content meets retailer-specific requirements",
    errorFreeChecks: 72,
    humanCaughtLowRisk: 12,
    humanCaughtHighRisk: 6,
    aiReviewSpeed: 168,
    auditLog: 25,
    status: "In Training",
    weeksWithoutHighRisk: 1,
  },
  {
    id: "task-9",
    categoryId: "AT9",
    taskCategory: "Required Legal Text Present",
    description: "Checks for presence of required legal disclaimers",
    errorFreeChecks: 46,
    humanCaughtLowRisk: 10,
    humanCaughtHighRisk: 6,
    aiReviewSpeed: 216,
    auditLog: 54,
    status: "In Training",
    weeksWithoutHighRisk: 3,
  },
  {
    id: "task-10",
    categoryId: "AT10",
    taskCategory: "Required Legal Text Correct",
    description: "Validates accuracy of legal text content",
    errorFreeChecks: 42,
    humanCaughtLowRisk: 5,
    humanCaughtHighRisk: 2,
    aiReviewSpeed: 282,
    auditLog: 28,
    status: "In Training",
    weeksWithoutHighRisk: 4,
  },

  // Out of Scope Tasks
  {
    id: "task-11",
    categoryId: "AT11",
    taskCategory: "Local/Regional Adaptation Correct",
    description: "Regional content adaptations - requires human expertise",
    errorFreeChecks: 0,
    humanCaughtLowRisk: 0,
    humanCaughtHighRisk: 0,
    aiReviewSpeed: 0,
    auditLog: 0,
    status: "Out of Scope",
    weeksWithoutHighRisk: 0,
  },
  {
    id: "task-12",
    categoryId: "AT12",
    taskCategory: "Correct Toolkit/KV/Campaign",
    description: "Campaign toolkit validation - requires human judgment",
    errorFreeChecks: 0,
    humanCaughtLowRisk: 0,
    humanCaughtHighRisk: 0,
    aiReviewSpeed: 0,
    auditLog: 0,
    status: "Out of Scope",
    weeksWithoutHighRisk: 0,
  },
];

// Helper to filter tasks by status
export function getTasksByStatus(status: AutomationStatus): AutomationTask[] {
  return MOCK_TASKS.filter((task) => task.status === status);
}

// Get status label for display (now the status IS the label)
export function getStatusLabel(status: AutomationStatus): string {
  return status;
}

// ============================================
// Filter Options for Audit Log
// ============================================

export interface FilterOption {
  label: string;
  value: string;
}

export const DATE_FILTER_OPTIONS: FilterOption[] = [
  { label: "This Week", value: "this_week" },
  { label: "This Month", value: "this_month" },
  { label: "This Quarter", value: "this_quarter" },
  { label: "Year to Date", value: "ytd" },
  { label: "Custom Range", value: "custom" },
];

export const DATE_FILTER_MENU_OPTIONS: FilterOption[] = [
  { label: "No Date Selected", value: "__clear__" },
  ...DATE_FILTER_OPTIONS,
];

export const TASK_CATEGORY_OPTIONS: FilterOption[] = [
  {
    label: "Previous Feedback Implemented",
    value: "Previous Feedback Implemented",
  },
  { label: "Copy Errors", value: "Copy Errors" },
  { label: "Brand Logo Usage", value: "Brand Logo Usage" },
  {
    label: "Validate SKU/Product Variants",
    value: "Validate SKU/Product Variants",
  },
  { label: "Claim Language Approved", value: "Claim Language Approved" },
  { label: "Brand Standard Adherence", value: "Brand Standard Adherence" },
  { label: "Assets Match Brief Specs", value: "Assets Match Brief Specs" },
  {
    label: "Retailer/Channel Requirements Met",
    value: "Retailer/Channel Requirements Met",
  },
  {
    label: "Required Legal Text Present",
    value: "Required Legal Text Present",
  },
  {
    label: "Required Legal Text Correct",
    value: "Required Legal Text Correct",
  },
];

export const TASK_CATEGORY_MENU_OPTIONS: FilterOption[] = [
  { label: "No Task Category Selected", value: "__clear__" },
  ...TASK_CATEGORY_OPTIONS,
];

export const OPERATING_MODE_OPTIONS: FilterOption[] = [
  { label: "Fully Automated", value: "Fully Automated" },
  { label: "In Training", value: "In Training" },
  { label: "Out of Scope", value: "Out of Scope" },
];

export const OPERATING_MODE_MENU_OPTIONS: FilterOption[] = [
  { label: "No Operating Mode Selected", value: "__clear__" },
  ...OPERATING_MODE_OPTIONS,
];

// Map task categories to their operating mode
export const TASK_CATEGORY_TO_MODE: Record<string, AutomationStatus> = {
  "Previous Feedback Implemented": "Fully Automated",
  "Copy Errors": "Fully Automated",
  "Brand Logo Usage": "Fully Automated",
  "Validate SKU/Product Variants": "Fully Automated",
  "Claim Language Approved": "Fully Automated",
  "Brand Standard Adherence": "Fully Automated",
  "Assets Match Brief Specs": "In Training",
  "Retailer/Channel Requirements Met": "In Training",
  "Required Legal Text Present": "In Training",
  "Required Legal Text Correct": "In Training",
  "Local/Regional Adaptation Correct": "Out of Scope",
  "Correct Toolkit/KV/Campaign": "Out of Scope",
};

// ============================================
// Audit Log Types and Mock Data
// ============================================

export interface WrikeTask {
  id: string;
  name: string;
  url: string;
}

export interface HumanCaughtError {
  reviewer: string;
  description: string;
}

export interface AuditLogEntry {
  id: string;
  wrikeTask: WrikeTask;
  punttTicketId: string;
  taskCategories: string[];
  humanCaughtErrors: HumanCaughtError[];
}

export const MOCK_AUDIT_LOG_ENTRIES: AuditLogEntry[] = [
  // First Wrike Task - Multiple entries
  {
    id: "audit-1",
    wrikeTask: {
      id: "9005216",
      name: "9005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=9005216",
    },
    punttTicketId: "690de7c9456f080fd4eadc42",
    taskCategories: ["Assets Match Brief Specs"],
    humanCaughtErrors: [
      { reviewer: "Jennifer", description: "please supply as jpeg" },
    ],
  },
  {
    id: "audit-2",
    wrikeTask: {
      id: "9005216",
      name: "9005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=9005216",
    },
    punttTicketId: "690de7c9456f080fd4eadc42",
    taskCategories: ["Previous Feedback Implemented"],
    humanCaughtErrors: [
      {
        reviewer: "Jennifer",
        description:
          "please change source to 'IRI total market 52 w/e 23 August 2025.'",
      },
    ],
  },
  {
    id: "audit-3",
    wrikeTask: {
      id: "9005216",
      name: "9005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=9005216",
    },
    punttTicketId: "690de7c9456f080fd4eadc42",
    taskCategories: ["Copy Errors"],
    humanCaughtErrors: [
      {
        reviewer: "Hannah",
        description: "this needs a space between choose and mountain",
      },
      { reviewer: "Hannah", description: "change to '10th to 23rd'" },
    ],
  },
  {
    id: "audit-4",
    wrikeTask: {
      id: "9005216",
      name: "9005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=9005216",
    },
    punttTicketId: "690de7c9456f080fd4eadc42",
    taskCategories: ["Brand Standard Adherence"],
    humanCaughtErrors: [
      { reviewer: "Laz", description: "please enlarge font" },
    ],
  },
  {
    id: "audit-5",
    wrikeTask: {
      id: "9005216",
      name: "9005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=9005216",
    },
    punttTicketId: "690de7c9456f080fd4eadc42",
    taskCategories: ["Validate SKU/Product Variants"],
    humanCaughtErrors: [],
  },
  {
    id: "audit-6",
    wrikeTask: {
      id: "9005216",
      name: "9005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=9005216",
    },
    punttTicketId: "690de7c9456f080fd4eadc42",
    taskCategories: ["Required Legal Text Correct"],
    humanCaughtErrors: [],
  },

  // Second Wrike Task - Same ID, different Puntt Ticket
  {
    id: "audit-7",
    wrikeTask: {
      id: "2005216",
      name: "2005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=2005216",
    },
    punttTicketId: "690e237138a57dd02092689",
    taskCategories: ["Assets Match Brief Specs"],
    humanCaughtErrors: [
      {
        reviewer: "Dominic",
        description: "packshot is blending into the black background",
      },
    ],
  },
  {
    id: "audit-8",
    wrikeTask: {
      id: "2005216",
      name: "2005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=2005216",
    },
    punttTicketId: "690e237138a57dd02092689",
    taskCategories: ["Previous Feedback Implemented"],
    humanCaughtErrors: [
      {
        reviewer: "Georgie",
        description:
          "I can still see all the ts and cs.... should have been removed?",
      },
    ],
  },
  {
    id: "audit-9",
    wrikeTask: {
      id: "2005216",
      name: "2005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=2005216",
    },
    punttTicketId: "690e237138a57dd02092689",
    taskCategories: ["Copy Errors"],
    humanCaughtErrors: [
      {
        reviewer: "Dominic",
        description:
          'change "Supports your muscles sip by sip" to "Support your muscles and reduce fatigue"',
      },
    ],
  },
  {
    id: "audit-10",
    wrikeTask: {
      id: "2005216",
      name: "2005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=2005216",
    },
    punttTicketId: "690e237138a57dd02092689",
    taskCategories: ["Brand Standard Adherence"],
    humanCaughtErrors: [],
  },
  {
    id: "audit-11",
    wrikeTask: {
      id: "2005216",
      name: "2005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=2005216",
    },
    punttTicketId: "690e237138a57dd02092689",
    taskCategories: ["Validate SKU/Product Variants"],
    humanCaughtErrors: [],
  },
  {
    id: "audit-12",
    wrikeTask: {
      id: "2005216",
      name: "2005216 - Cross-Brand - Evian & Volvic Case Cost Posters - Other Food Services - CRO-50150825",
      url: "https://www.wrike.com/open.htm?id=2005216",
    },
    punttTicketId: "690e237138a57dd02092689",
    taskCategories: ["Required Legal Text Correct"],
    humanCaughtErrors: [],
  },

  // Third Wrike Task
  {
    id: "audit-13",
    wrikeTask: {
      id: "2057836",
      name: "2057836 - Winter Competition Banner - Cross-Brand - Business to Consumer (B2C) - MKT-22001736",
      url: "https://www.wrike.com/open.htm?id=2057836",
    },
    punttTicketId: "690a0bee0682b72dc194a4bb",
    taskCategories: ["Assets Match Brief Specs"],
    humanCaughtErrors: [
      {
        reviewer: "Jennifer",
        description: "send as images because I cannot access PDFs",
      },
    ],
  },
  {
    id: "audit-14",
    wrikeTask: {
      id: "2057836",
      name: "2057836 - Winter Competition Banner - Cross-Brand - Business to Consumer (B2C) - MKT-22001736",
      url: "https://www.wrike.com/open.htm?id=2057836",
    },
    punttTicketId: "690a0bee0682b72dc194a4bb",
    taskCategories: ["Copy Errors"],
    humanCaughtErrors: [
      {
        reviewer: "Jennifer",
        description:
          'files are missing the required line "Win family tickets to..."',
      },
    ],
  },
];

// Helper to group audit entries by Wrike Task
export function groupAuditEntriesByWrikeTask(
  entries: AuditLogEntry[],
): Map<string, AuditLogEntry[]> {
  const grouped = new Map<string, AuditLogEntry[]>();

  entries.forEach((entry) => {
    const key = `${entry.wrikeTask.id}-${entry.punttTicketId}`;
    const existing = grouped.get(key) || [];
    grouped.set(key, [...existing, entry]);
  });

  return grouped;
}
